import React, {
  useState,
  useRef,
  useEffect,
  memo,
  useImperativeHandle,
  useCallback,
} from "react";
import { List, RowComponentProps, ListImperativeAPI } from "react-window";
import { Photo } from "../types";
import { debounce } from "../utils/optimizeRendering";
import ImageLoader from "./ImageLoader";
import { Language } from "../translations";

export interface VirtualizedGalleryProps {
  photos: Photo[];
  onPhotoClick: (index: number) => void;
  onVisiblePhotoChange?: (timestamp: string, isFirst: boolean) => void;
  ref?: React.RefObject<VirtualizedGalleryApi | null>;
  showDownloadButton?: boolean;
  onDownloadPhoto?: (url: string, filename: string) => void;
  language?: Language;
}

export interface VirtualizedGalleryApi {
  scrollToPhoto: (photoIndex: number) => void;
}

interface PhotoRowProps {
  photos: Photo[];
  columnCount: number;
  columnWidth: number;
  itemGap: number;
  onPhotoClick: (index: number) => void;
  showDownloadButton?: boolean;
  onDownloadPhoto?: (url: string, filename: string) => void;
  language?: Language;
}

// Helper component for lazy loading images
const LazyImage = memo(
  ({
    photo,
    onClick,
    showDownloadButton,
    onDownloadPhoto,
    language = "en",
  }: {
    photo: Photo;
    onClick: () => void;
    showDownloadButton?: boolean;
    onDownloadPhoto?: (url: string, filename: string) => void;
    language?: Language;
  }) => {
    // const [loaded, setLoaded] = useState(false);
    // const imgRef = useRef<HTMLImageElement>(null);
    // const observerRef = useRef<IntersectionObserver | null>(null);

    // useEffect(() => {
    //   // Create observer for this image
    //   observerRef.current = new IntersectionObserver(
    //     (entries) => {
    //       if (entries[0].isIntersecting) {
    //         // Only set src when the image is visible
    //         if (imgRef.current && !imgRef.current.src) {
    //           imgRef.current.src = photo.thumbnailUrl;
    //         }

    //         // Stop observing once it's loaded
    //         if (observerRef.current && imgRef.current) {
    //           observerRef.current.unobserve(imgRef.current);
    //         }
    //       }
    //     },
    //     { threshold: 0.1 }
    //   );

    //   // Start observing
    //   if (imgRef.current && observerRef.current) {
    //     observerRef.current.observe(imgRef.current);
    //   }

    //   // Clean up observer
    //   return () => {
    //     if (observerRef.current) {
    //       observerRef.current.disconnect();
    //     }
    //   };
    // }, [photo.thumbnailUrl]);

    // Get file name for display
    const getImageName = (url: string) => {
      const pathParts = url.split("/");
      return pathParts[pathParts.length - 1];
    };

    const filename = getImageName(photo.url);

    const handleDownload = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (onDownloadPhoto) {
        onDownloadPhoto(photo.url, filename);
      }
    };

    return (
      <div
        // className={`photo-item ${loaded ? 'loaded' : ''}`}
        className={`photo-item loaded`}
        data-timestamp={photo.takenAt}
        onClick={onClick}
      >
        <div className="photo-container">
          <ImageLoader
            // ref={imgRef}
            alt={`Photo ${photo.id}`}
            data-src={photo.thumbnailUrl}
            src={photo.thumbnailUrl} //TODO
            // onLoad={() => setLoaded(true)}
            language={language}
          />
          <div className="photo-name">{filename}</div>
          <div className="photo-time">
            {new Date(photo.takenAt).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
              hour12: false, // Use 24-hour format
              timeZone: "Europe/Warsaw",
            })}
          </div>
          {showDownloadButton && (
            <button className="download-button" onClick={handleDownload}>
              ↓
            </button>
          )}
        </div>
      </div>
    );
  },
);

// Main Virtualized Gallery component
const VirtualizedGallery: React.FC<VirtualizedGalleryProps> = ({
  photos,
  onPhotoClick,
  onVisiblePhotoChange,
  ref,
  showDownloadButton = false,
  onDownloadPhoto,
  language = "en",
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [, forceUpdate] = useState({});

  // Options for virtualization

  const overscanCount = 1;
  const defaultColumnWidth = 300;
  const itemGap = 10;
  const defaultItemHeight = 210;

  // const sizeMapRef = useRef<{[key: number]: number}>({});

  // Calculate actual columns based on container width
  const calculateActualColumns = useCallback(() => {
    if (!containerRef.current) return 4;

    const container = containerRef.current;
    const containerWidth = container.clientWidth;
    const availableWidth = containerWidth;
    const calculatedColumnCount = Math.max(
      1,
      Math.floor((availableWidth - itemGap) / (defaultColumnWidth + itemGap)),
    );

    return calculatedColumnCount;
  }, [defaultColumnWidth, itemGap]);

  const [columnCount, setActualColumnCount] = useState(
    calculateActualColumns(),
  );

  // Calculate total rows needed
  const rowCount = Math.ceil(photos.length / columnCount);

  // Calculate item size (height) based on image aspect ratio
  const getItemHeight = useCallback(
    (_index: number): number => {
      // if (sizeMapRef.current[index]) {
      //   return sizeMapRef.current[index];
      // }

      // // Calculate row height based on the tallest item in the row
      // const rowStartIndex = index * actualColumnCount;
      // let maxHeight = defaultItemHeight;

      // for (let i = 0; i < actualColumnCount; i++) {
      //   const photoIndex = rowStartIndex + i;
      //   if (photoIndex >= photos.length) break;

      //   const photo = photos[photoIndex];
      //   if (photo.width && photo.height) {
      //     const effectiveColumnWidth = columnWidth - itemGap;
      //     const aspectRatio = photo.width / photo.height;
      //     const calculatedHeight = effectiveColumnWidth / aspectRatio;
      //     maxHeight = Math.max(maxHeight, calculatedHeight);
      //   }
      // }

      // // Add a little extra space for image caption/info
      // const finalHeight = maxHeight + 40;
      // sizeMapRef.current[index] = finalHeight;
      // return finalHeight;
      return defaultItemHeight;
    },
    [photos, columnCount, defaultItemHeight, defaultColumnWidth, itemGap],
  );

  // Reset size cache when photos or column count changes
  // useEffect(() => {
  //   sizeMapRef.current = {};
  //   if (listRef.current) {
  //     listRef.current.resetAfterIndex(0);
  //   }
  // }, [photos, actualColumnCount]);

  // Update container dimensions on resize
  useEffect(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;

    const updateDimensions = debounce(() => {
      if (!container) return;
      // setContainerWidth(container.clientWidth);
      // setContainerHeight(container.clientHeight);

      const newColumnCount = calculateActualColumns();
      if (newColumnCount !== columnCount) {
        setActualColumnCount(newColumnCount);
      }
      // force react component refresh
      forceUpdate({});
    }, 300);

    // Initial update
    updateDimensions();

    // Listen for resize events
    window.addEventListener("resize", updateDimensions);

    return () => {
      window.removeEventListener("resize", updateDimensions);
    };
  }, [containerRef, calculateActualColumns, columnCount]);

  // Get the photos to render in the current row
  const getRowPhotos = useCallback(
    (rowIndex: number): Photo[] => {
      const startIndex = rowIndex * columnCount;
      return photos.slice(startIndex, startIndex + columnCount);
    },
    [photos, columnCount],
  );

  const listRef = useRef<ListImperativeAPI>(null);

  useImperativeHandle(ref, () => {
    return {
      scrollToPhoto: (photoIndex: number) => {
        if (!listRef.current) return;

        const rowIndex = Math.floor(photoIndex / columnCount);
        listRef.current.scrollToRow({ index: rowIndex, align: "start" });
      },
    };
  }, [columnCount, listRef]);

  if (!photos.length) {
    return <div>No photos to display</div>;
  }

  return (
    <div
      ref={containerRef}
      className="gallery"
      style={{ width: "100%", height: "100%", overflow: "auto" }}
    >
      {containerRef.current && (
        <List<PhotoRowProps>
          style={{ minHeight: "100%" }}
          listRef={listRef}
          overscanCount={overscanCount}
          onRowsRendered={({ startIndex }) => {
            if (onVisiblePhotoChange) {
              const rowPhotos = getRowPhotos(startIndex);
              onVisiblePhotoChange(
                rowPhotos[rowPhotos.length - 1]?.takenAt,
                startIndex === 0,
              );
            }
          }}
          rowComponent={PhotoRow}
          rowCount={rowCount}
          rowHeight={getItemHeight}
          rowProps={{
            photos,
            columnCount,
            columnWidth: defaultColumnWidth,
            itemGap,
            onPhotoClick,
            showDownloadButton,
            onDownloadPhoto,
            language,
          }}
        ></List>
      )}
    </div>
  );
};

// Component to render a row of photos
function PhotoRow({
  photos,
  index,
  style,
  columnCount,
  columnWidth,
  itemGap,
  onPhotoClick,
  showDownloadButton,
  onDownloadPhoto,
  language,
}: RowComponentProps<PhotoRowProps>) {
  const startIndex = index * columnCount;
  const itemsInRow = Math.min(columnCount, photos.length - startIndex);
  const effectiveColumnWidth = columnWidth;

  return (
    <div
      style={{
        ...style,
        display: "flex",
        gap: `${itemGap}px`,
        padding: `${itemGap}px`,
        width: `calc(100%-${2 * itemGap}px)`,
      }}
    >
      {Array.from({ length: itemsInRow }).map((_, i) => {
        const photoIndex = startIndex + i;
        return photoIndex < photos.length ? (
          <div
            key={photos[photoIndex].id}
            style={{
              width: effectiveColumnWidth,
              position: "relative",
              marginTop: `${itemGap}px`,
            }}
          >
            <LazyImage
              photo={photos[photoIndex]}
              onClick={() => onPhotoClick(photoIndex)}
              showDownloadButton={showDownloadButton}
              onDownloadPhoto={onDownloadPhoto}
              language={language}
            />
          </div>
        ) : null;
      })}
    </div>
  );
}

export default VirtualizedGallery;
